import { format } from 'date-fns';
import type { VacationBalance, VacationRequest, VacationRequestStatus } from '../models/vacation.types';
import { LEGACY_PERIODS_CUTOFF_DATE } from '../vacations.definitions';

export interface VacationStatementRow {
    date: string;
    description: string;
    credit?: number;
    debit?: number;
    note?: string;
}

// Status que não aparecem no extrato
const DISCARDED_STATUSES = ['Rejected'];

// Mapa de status para rótulo descritivo
function getRequestStatusLabel(status: VacationRequestStatus): string {
    switch (status) {
        case 'Draft':
            return 'aguardando envio';
        case 'PendingManager':
            return 'aguardando gestor';
        case 'PendingDP':
            return 'aguardando DP';
        case 'Scheduled':
            return 'agendada';
        case 'OnVacation':
            return 'em gozo';
        case 'Completed':
            return 'concluída';
        default:
            return status;
    }
}

/**
 * Constrói a razão de férias item a item: uma linha por evento real.
 * Diferente do extrato agregado que mostra apenas os períodos aquisitivos,
 * esta função detalha cada solicitação e lançamento histórico.
 *
 * Ordem: créditos (períodos aquisitivos), depois débitos (gozos, agendamentos, abonos),
 * cronologicamente dentro de cada tipo.
 */
export function buildVacationStatementRows(
    balance: VacationBalance | null,
    vacationPeriods: Array<{ startDate?: string; endDate?: string; days?: number; observations?: string }> = [],
    requests: VacationRequest[] = []
): VacationStatementRow[] {
    const rows: VacationStatementRow[] = [];

    // 1. Créditos: um por período aquisitivo fechado
    if (balance) {
        const completed = balance.periods.filter((period) => period.status !== 'Accruing');
        for (const period of completed) {
            const startYear = period.startDate.split('-')[0];
            const endYear = period.endDate.split('-')[0];
            const periodLabel = startYear === endYear ? startYear : `${startYear}–${endYear}`;

            rows.push({
                date: period.endDate, // Data do fim do período (quando o direito se consolida)
                description: `Crédito Período Aquisitivo (${periodLabel})`,
                credit: period.entitledDays,
            });
        }
    }

    // 2. Débitos históricos: lançamentos legados (férias gozadas antes da migração)
    const cutoffDate = LEGACY_PERIODS_CUTOFF_DATE;
    for (const vp of vacationPeriods) {
        if (!vp.startDate || !vp.endDate) continue;
        if (vp.startDate >= cutoffDate) continue; // Ignorar pós-cutoff

        // Validar datas
        if (vp.startDate > vp.endDate) continue;

        rows.push({
            date: vp.startDate,
            description: `Férias gozadas (histórico)${vp.observations ? ` · ${vp.observations}` : ''}`,
            debit: vp.days || 0,
        });
    }

    // 3. Débitos de solicitações: um por segmento
    for (const request of requests) {
        if (DISCARDED_STATUSES.includes(request.status)) continue;

        const statusLabel = getRequestStatusLabel(request.status);

        // Cada segmento é uma linha separada
        for (const segment of request.segments) {
            if (!segment.startDate || !segment.endDate) continue;

            rows.push({
                date: segment.startDate,
                description: `Férias ${statusLabel}`,
                debit: segment.days,
                note: request.id, // Referência para rastreamento
            });
        }

        // Abono de férias (se houver)
        if (request.sellRequest.requested && request.sellRequest.days > 0 && request.sellRequest.status !== 'Rejected') {
            const abonoStatusLabel = request.sellRequest.status === 'Approved' ? 'aprovado' : 'aguardando aprovação';
            rows.push({
                date: format(request.requestedAt ? request.requestedAt.toDate() : new Date(), 'yyyy-MM-dd'),
                description: `Abono pecuniário ${abonoStatusLabel}`,
                debit: request.sellRequest.days,
                note: request.id,
            });
        }
    }

    // Ordenar cronologicamente
    rows.sort((a, b) => a.date.localeCompare(b.date));

    return rows;
}

/**
 * Calcula o saldo final verificando se a soma fecha com o saldo reportado.
 * Retorna o total de créditos menos débitos, e um aviso se houver divergência.
 */
export function calculateStatementBalance(rows: VacationStatementRow[]): {
    totalCredit: number;
    totalDebit: number;
    calculated: number;
    hasDiscrepancy: boolean;
} {
    const totalCredit = rows.reduce((sum, row) => sum + (row.credit || 0), 0);
    const totalDebit = rows.reduce((sum, row) => sum + (row.debit || 0), 0);
    const calculated = totalCredit - totalDebit;

    return {
        totalCredit,
        totalDebit,
        calculated,
        hasDiscrepancy: false, // Será preenchido pelo componente com base no balance.balanceDays
    };
}
